<template>
  <BContent
    v-loading="loading"
    class="content"
  >
    <template #navbar>
      <VWait
        class="lead-view-area"
        for="none"
        :class="{ loaded: !$wait.is('getLeadViewActionWait') }"
        :visible="$wait.is('getLeadViewActionWait')"
      >
        <template #waiting>
          <BContentLoader>
            <rect
              x="0"
              y="0"
              rx="8"
              ry="8"
              width="400"
              height="36"
            />
          </BContentLoader>
        </template>
        <LeadViewSelect
          :selected-lead-view="leadView"
          @select-lead-view="handleSelectLeadView"
        />
        <BCustomIconText
          text-size="mini"
          hover
          icon-size="large"
          icon-class="b-call-analytics"
          class="ml-200"
          @click="moveAnalytics"
        >
          {{ $t('callAnalytics.title') }}
        </BCustomIconText>
        <BCustomIconText
          v-if="leadView.id"
          hover
          text-size="mini"
          icon-size="large"
          icon-class="b-list-update"
          class="ml-200"
          @click="$modal.show('LeadViewUpdateModal')"
        >
          {{ $t('general.overwriteSave') }}
        </BCustomIconText>
        <BTooltip top>
          <BCustomIconText
            hover
            text-size="mini"
            icon-size="large"
            icon-class="b-list-new"
            class="ml-200"
            @click="$modal.show('leadViewCreateModal')"
          >
            {{ $t('leadList.save.title') }}
          </BCustomIconText>
          <template #content>
            <div class="save-text">
              {{ $t('leadList.save.description') }}
            </div>
          </template>
        </BTooltip>
      </VWait>
      <div v-if="isAvailableSalesforceApiFeature">
        <BBtn
          type="primary"
          size="mini"
          class="mr-200"
          @click="$modal.show('SfLeadDataCheckModal')"
        >
          {{ $t('leadList.syncSalesforce.title') }}
        </BBtn>
      </div>
      <BDivider
        v-if="visibleMailButton"
        direction="vertical"
      />
      <BTooltip
        v-if="visibleMailButton"
        top
        :content="$t('mail.mailCreate')"
      >
        <BCustomIcon
          class="mr-100 ml-100"
          size="small"
          icon-class="b-mail"
          hover
          @click="showBulkMailForm"
        />
      </BTooltip>
      <BDivider direction="vertical" />
      <BTooltip
        top
        :content="$t('callList.import.title')"
      >
        <div>
          <BMenu
            v-model="isImportMenuVisible"
            placement="bottom"
          >
            <template #activator>
              <BCustomIcon
                class="mr-100 ml-100"
                size="small"
                icon-class="b-import"
                hover
              />
            </template>
            <BCard full>
              <div class="py-100">
                <BBtn
                  flat
                  fit
                  size="small"
                  @click="showImportModal('callTargetSetting')"
                >
                  <span>{{ $t('leadList.import.type.single') }}</span>
                </BBtn>
                <BBtn
                  flat
                  fit
                  size="small"
                  @click="showImportModal('CsvImportModal')"
                >
                  <span>{{ $t('leadList.import.type.csv') }}</span>
                </BBtn>
                <BBtn
                  flat
                  fit
                  size="small"
                  @click="showImportModal('ExcelImportModal')"
                >
                  <span>{{ $t('leadList.import.type.excel') }}</span>
                </BBtn>
                <BBtn
                  v-if="isAvailableSalesforceApiFeature"
                  flat
                  fit
                  size="small"
                  class="toggle-btn"
                  @click="isSfOpen = !isSfOpen"
                >
                  <BIcon
                    class="toggle-icon"
                    :class="{ 'is-open': isSfOpen }"
                  >
                    arrow_drop_up
                  </BIcon>
                  <span>{{ $t('leadList.import.type.salesforce') }}</span>
                </BBtn>
                <BBtn
                  v-if="isSfOpen"
                  flat
                  fit
                  size="small"
                  @click="showImportModal('SalesforceReportModal')"
                >
                  <span>{{ $t('leadList.import.type.sfReport') }}</span>
                </BBtn>
                <BBtn
                  v-if="isSfOpen"
                  flat
                  fit
                  size="small"
                  @click="showImportModal('SalesforceSingleImportModal')"
                >
                  <span>{{ $t('leadList.import.type.sfId') }}</span>
                </BBtn>
              </div>
            </BCard>
          </BMenu>
        </div>
      </BTooltip>
      <BTooltip
        top
        :content="$t('callList.update.title')"
      >
        <div>
          <BMenu
            v-model="isOverwriteMenuVisible"
            placement="bottom"
          >
            <template #activator>
              <BCustomIcon
                class="update-icon"
                size="small"
                icon-class="b-update"
                hover
              />
            </template>
            <BCard full>
              <div class="py-100">
                <BBtn
                  flat
                  fit
                  size="small"
                  @click="showOverwriteModal('CsvOverwriteModal')"
                >
                  <span>{{ $t('callList.update.type.csv') }}</span>
                </BBtn>
                <BBtn
                  flat
                  fit
                  size="small"
                  @click="showOverwriteModal('ExcelOverwriteModal')"
                >
                  <span>{{ $t('callList.update.type.excel') }}</span>
                </BBtn>
              </div>
            </BCard>
          </BMenu>
        </div>
      </BTooltip>
      <BTooltip
        v-if="authorize(['owner'])"
        top
        :content="$t('callList.export.title')"
      >
        <BCustomIcon
          class="mx-100"
          size="small"
          icon-class="b-export"
          hover
          @click="$modal.show('LeadExportModal')"
        />
      </BTooltip>
      <BMoreMenu
        v-model="isDeleteMenuVisible"
        placement="bottom"
      >
        <BBtn
          flat
          fit
          size="small"
          @click="showDeleteModal('AllLeadDeleteModal')"
        >
          <span class="lead-delete">{{ $t('callTarget.destroyAll.doing') }}</span>
        </BBtn>
      </BMoreMenu>
    </template>
    <div class="call-list-template">
      <LeadCsvImportStatus @success="getCallTargets" />
      <LeadExcelImportStatus @success="getCallTargets" />
      <LeadCsvOverwriteStatus @success="getCallTargets" />
      <LeadExcelOverwriteStatus @success="getCallTargets" />
      <LeadDeleteStatus @success="getCallTargets" />
      <LeadMergeStatus @success="getCallTargets" />
      <CustomFieldDestroyStatus />
      <CustomFieldOptionDestroyStatus />
      <SfLeadDataCheckStatus
        v-if="isAvailableSalesforceApiFeature"
        @success="getCallTargets"
      />
      <LeadSfImportStatus
        v-if="isAvailableSalesforceApiFeature"
        @success="getCallTargets"
      />
      <div class="lead-list-control">
        <ListPageControl>
          <FilterSelect />
          <ListColumnSelect
            :has-custom-default-setting="hasCustomDefaultSetting"
            :save-as-default-loading="wait.is('saveAsDefaultWait')"
            @click:reset-columns="handleResetColumns"
            @click:save-as-default="handleUpdateDisplayColumns"
          />
        </ListPageControl>
      </div>
      <div
        class="lead-list-area"
        :class="{ 'with-footer': selectedCallTargetIds.length > 0 }"
      >
        <LeadListChild
          :call-targets="callTargets"
          :call-targets-meta="callTargetsMeta"
          :custom-fields="customFields"
          @update:selected-call-target-ids="handleSelectedCallTargetIdsUpdate"
        />
      </div>
    </div>
    <CsvImportModal />
    <ExcelImportModal />
    <CsvOverwriteModal />
    <ExcelOverwriteModal />
    <template v-if="authorize(['owner'])">
      <LeadExportModal
        :number-of-target-leads="callTargetsMeta.totalCount"
        :number-of-leads="callTargetsCount"
        :lead-list-filter-params="leadListFilterParams"
      />
    </template>
    <LeadViewCreateModal />
    <LeadViewUpdateModal />
    <MailFormTransition :is-visible="mailFormVisible">
      <BulkMailForm
        v-model:bulk-mail="bulkMail"
        :can-duplicate="false"
        @cancel="hiddenMailForm"
        @bulk-mail-change="bulkMail = $event"
      />
    </MailFormTransition>
    <AllLeadDeleteModal />
    <div v-if="isAvailableSalesforceLinkFeature">
      <SfLeadDataCheckModal />
      <SalesforceReportModal />
      <SalesforceSingleImportModal />
    </div>
    <template #footer>
      <LeadBulkActionsFooter
        v-if="isExistCallTargets"
        class="footer-content"
        :selected-call-target-ids="selectedCallTargetIds"
        @click-bulk-update="handleClickBulkUpdate"
      />
    </template>
    <BulkAssignOperatorModal :selected-call-target-ids="selectedCallTargetIds" />
    <BulkUpdateFreeTagsModal :selected-call-target-ids="selectedCallTargetIds" />
    <BulkUpdateLeadSourcesModal :selected-call-target-ids="selectedCallTargetIds" />
  </BContent>
</template>

<script lang="ts">
import lodash from 'lodash';
import { defineComponent } from 'vue';
import { mapWaitingActions } from 'vue-wait';
import { mapGetters } from 'vuex';
import type { LeadView } from '@/api/openapi';
import { LeadViewLogApiService } from '@/api/user/resources/lead_view_log';
import CustomFieldDestroyStatus from '@/components/organisms/user/CustomFieldDestroyStatus.vue';
import CustomFieldOptionDestroyStatus from '@/components/organisms/user/CustomFieldOptionDestroyStatus.vue';
import LeadViewSelect from '@/components/organisms/user/general/leadViewSelect/LeadViewSelect.vue';
import LeadViewCreateModal from '@/components/organisms/user/general/modal/leadView/LeadViewCreateModal.vue';
import LeadMergeStatus from '@/components/organisms/user/lead/duplicatedList/LeadMergeStatus.vue';
import LeadExportModal from '@/components/organisms/user/LeadExportModal.vue';
import LeadCsvImportStatus from '@/components/organisms/user/leadImport/LeadCsvImportStatus.vue';
import LeadCsvOverwriteStatus from '@/components/organisms/user/leadImport/LeadCsvOverwriteStatus.vue';
import LeadDeleteStatus from '@/components/organisms/user/leadImport/LeadDeleteStatus.vue';
import LeadExcelImportStatus from '@/components/organisms/user/leadImport/LeadExcelImportStatus.vue';
import LeadExcelOverwriteStatus from '@/components/organisms/user/leadImport/LeadExcelOverwriteStatus.vue';
import LeadSfImportStatus from '@/components/organisms/user/leadImport/LeadSfImportStatus.vue';
import SfLeadDataCheckStatus from '@/components/organisms/user/leadImport/SfLeadDataCheckStatus.vue';
import AllLeadDeleteModal from '@/components/organisms/user/leadList/AllLeadDeleteModal.vue';
import BulkAssignOperatorModal from '@/components/organisms/user/leadList/BulkAssignOperatorModal.vue';
import BulkUpdateFreeTagsModal from '@/components/organisms/user/leadList/BulkUpdateFreeTagsModal.vue';
import BulkUpdateLeadSourcesModal from '@/components/organisms/user/leadList/BulkUpdateLeadSourcesModal.vue';
import FilterSelect from '@/components/organisms/user/leadList/filter/FilterSelect.vue';
import LeadBulkActionsFooter from '@/components/organisms/user/leadList/LeadBulkActionsFooter.vue';
import LeadListChild from '@/components/organisms/user/leadList/LeadList.vue';
import LeadViewUpdateModal from '@/components/organisms/user/leadList/LeadViewUpdateModal.vue';
import ListColumnSelect from '@/components/organisms/user/leadList/ListColumnSelect.vue';
import ListPageControl from '@/components/organisms/user/leadList/ListPageControl.vue';
import CsvImportModal from '@/components/organisms/user/leadList/modal/CsvImportModal.vue';
import CsvOverwriteModal from '@/components/organisms/user/leadList/modal/CsvOverwriteModal.vue';
import ExcelImportModal from '@/components/organisms/user/leadList/modal/ExcelImportModal.vue';
import ExcelOverwriteModal from '@/components/organisms/user/leadList/modal/ExcelOverwriteModal.vue';
import SalesforceReportModal from '@/components/organisms/user/leadList/modal/SalesforceReportModal.vue';
import SalesforceSingleImportModal from '@/components/organisms/user/leadList/modal/SalesforceSingleImportModal.vue';
import SfLeadDataCheckModal from '@/components/organisms/user/leadList/SfLeadDataCheckModal.vue';
import BulkMailForm from '@/components/organisms/user/mail/BulkMailForm.vue';
import MailFormTransition from '@/components/organisms/user/mail/common/drawer/MailFormTransition.vue';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useCurrentUserProvider } from '@/composable/current-user';
import { useIntercom } from '@/composable/intercom';
import { useRouterLink } from '@/composable/router-link';
import { camelcaseFilterParamsKeys } from '@/composable/user/leadList/lead-filter';
import { useLeadViewState } from '@/composable/user/leadList/use-lead-view-state';
import { useWait } from '@/composable/vue-wait';

export default defineComponent({
  components: {
    LeadExportModal,
    CsvImportModal,
    ExcelImportModal,
    LeadCsvImportStatus,
    LeadExcelImportStatus,
    CsvOverwriteModal,
    ExcelOverwriteModal,
    LeadCsvOverwriteStatus,
    LeadExcelOverwriteStatus,
    SfLeadDataCheckStatus,
    LeadBulkActionsFooter,
    BulkAssignOperatorModal,
    BulkUpdateFreeTagsModal,
    BulkUpdateLeadSourcesModal,
    LeadListChild,
    LeadViewCreateModal,
    LeadViewUpdateModal,
    LeadViewSelect,
    FilterSelect,
    ListColumnSelect,
    BulkMailForm,
    AllLeadDeleteModal,
    LeadDeleteStatus,
    SfLeadDataCheckModal,
    SalesforceReportModal,
    LeadSfImportStatus,
    MailFormTransition,
    LeadMergeStatus,
    CustomFieldDestroyStatus,
    CustomFieldOptionDestroyStatus,
    ListPageControl,
    SalesforceSingleImportModal,
  },
  setup(props) {
    useIntercom();
    const { inject: injectCurrentUser } = useCurrentUserProvider();
    const { authorize } = injectCurrentUser();
    const {
      isAvailableSalesforceApiFeature,
      isAvailableSalesforceLinkFeature,
      isAvailableBulkMailFeature,
    } = useAvailableFeatureCheck();
    const {
      isConditionChanged,
      setDefaultDisplayColumns,
      hasCustomDefaultSetting,
      updateDisplayColumns,
    } = useLeadViewState();
    const { linkToOrReload } = useRouterLink();
    const { doActionWithWait, wait } = useWait();
    return {
      authorize,
      isAvailableSalesforceApiFeature,
      isAvailableSalesforceLinkFeature,
      isAvailableBulkMailFeature,
      isConditionChanged,
      setDefaultDisplayColumns,
      linkToOrReload,
      hasCustomDefaultSetting,
      updateDisplayColumns,
      doActionWithWait,
      wait,
    };
  },
  data() {
    return {
      mailFormVisible: false,
      taskTimerObj: null,
      isImportMenuVisible: false,
      isOverwriteMenuVisible: false,
      isDeleteMenuVisible: false,
      bulkMail: {},
      selectedCallTargetIds: [],
      isSfOpen: false,
      logApi: new LeadViewLogApiService(),
    };
  },
  computed: {
    ...mapGetters('user', [
      'callTargets',
      'callTargetsMeta',
      'customFields',
      'leadView',
      'leadExcelImportTasks',
      'leadCsvImportTasks',
      'leadCsvOverwriteTasks',
      'leadExcelOverwriteTasks',
      'sfReportImportTasks',
      'callTargetsCount',
    ]),
    ...mapGetters('userUi', [
      'leadListPageParams',
      'leadListFilterParams',
      'pendingCsvImportTaskIds',
      'pendingCsvOverwriteTaskIds',
      'pendingExcelImportTaskIds',
      'pendingExcelOverwriteTaskIds',
      'pendingSfImportTaskIds',
    ]),
    isExistCallTargets() {
      return this.callTargets.length > 0;
    },
    visibleMailButton() {
      return this.isAvailableBulkMailFeature && this.leadView.id;
    },
    loading() {
      return this.$wait.is(['getCallTargetsWait', 'getCallTargetsBackgroundWait']);
    },
  },
  watch: {
    // NOTE: リードビューの切替は表示列・フィルタ・ソートを総入れ替えするため初期化をやり直す。
    // ただしLeadViewCreateModalは、setLeadViewActionで適用済みのリードビューのidをURLに記録するだけなので、
    // 既に適用済みのidと一致する場合は再初期化しない。
    '$route.query.leadViewId'(leadViewIdInQuery) {
      if ((Number(leadViewIdInQuery) || null) === (this.leadView?.id ?? null)) return;
      this.initializeLeadListPage();
    },
  },
  async created() {
    this.setupTaskTimer();
    await this.initializeLeadListPage();
  },
  methods: {
    ...mapWaitingActions('user', {
      getCallTargetsAction: {
        action: 'getCallTargetsAction',
        loader: 'getCallTargetsWait',
      },
      getCallTargetsMetaAction: {
        action: 'getCallTargetsMetaAction',
        loader: 'getCallTargetsMetaWait',
      },
      getCallTargetsBackgroundAction: {
        action: 'getCallTargetsAction',
        loader: 'getCallTargetsBackgroundWait',
      },
      getUsersAction: 'getUsersWait',
      getLeadViewAction: {
        action: 'getLeadViewAction',
        loader: 'getLeadViewActionWait',
      },
      clearLeadViewAction: 'clearLeadViewAction',
      getBulkMailCampaignNamesAction: {
        action: 'getBulkMailCampaignNamesAction',
        loader: 'getBulkMailCampaignNamesWait',
      },
      getCallTargetsCountAction: 'getCallTargetsCountWait',
    }),
    ...mapWaitingActions('userUi', {
      setLeadListFilterParamsAction: 'setLeadListFilterParamsWait',
      setLeadListPageParamsAction: 'setLeadListFilterParamsWait',
      setFilterControlPanelVisibleAction: 'setFilterControlPanelVisibleWait',
      setLeadListSelectedColumnsAction: 'setLeadListSelectedColumnsWait',
      setLeadListSortKeyAction: 'setLeadListSortKeyWait',
      setLeadListSortOrderAction: 'setLeadListSortOrderWait',
      setPendingCsvImportTaskIdsAction: 'setPendingCsvImportTaskIdsWait',
      setPendingExcelImportTaskIdsAction: 'setPendingExcelImportTaskIdsWait',
      setPendingCsvOverwriteTaskIdsAction: 'setPendingCsvOverwriteTaskIdsWait',
      setPendingExcelOverwriteTaskIdsAction: 'setPendingExcelOverwriteTaskIdsWait',
      setPendingSfImportTaskIdsAction: 'setPendingSfImportTaskIdsWait',
    }),
    // NOTE: RouterViewのkeyでの再マウントに頼らず、created()とleadViewIdのwatchから明示的に呼ぶ
    async initializeLeadListPage() {
      await this.doActionWithWait('initLeadListPage', async () => {
        await this.clearLeadViewAction();

        // NOTE: クエリはvue-routerで駆動しているのでroute.queryから読む。
        // currentDisplayNumberはURLに載せていない（バックエンドが参照せず変更UIも無い）ため読まない。
        const pageParams: { currentPage?: number } = {};
        const { currentPage, leadViewId: leadViewIdInQuery } = this.$route.query;
        if (currentPage) pageParams.currentPage = parseInt(currentPage as string);
        this.setLeadListPageParamsAction(pageParams);

        const results = [];
        const leadViewId = parseInt(leadViewIdInQuery as string);
        if (leadViewId) {
          await this.getLeadView(leadViewId);
          this.setLeadViewSettings(this.leadView);
        } else {
          await this.setDefaultDisplayColumns();
        }

        results.push(this.getCallTargets());
        results.push(this.getUsersAction());
        results.push(this.getBulkMailCampaignNamesAction());

        await Promise.all(results);

        this.getCallTargetsCountAction();
      });
    },
    async moveAnalytics(){
      if (this.isConditionChanged) {
        const isWarningAccepted = await this.$bitterAlert.show({
          title: this.$t('general.confirm.text'),
          text: this.$t('leadView.changeCaution'),
        });
        if (!isWarningAccepted) return;
      }
      this.$router.push({ name: 'CallAnalytics', query: { leadViewId: this.leadView.id } });
    },
    async getLeadView(leadViewId) {
      try {
        await this.getLeadViewAction({
          leadViewId,
          errorHandlers: { 404 () {} },
        });
      } catch (e) {
        this.$toast.show({
          message: this.$t('general.fail.notExists', {
            target: this.$t('leadView.title'),
          }),
          type: 'error',
        });
      }
    },
    async getCallTargetsAndMeta() {
      await this.getCallTargetsBackgroundAction();
      this.getCallTargetsMetaAction();
    },
    async getCallTargets() {
      if (this.callTargets.length > 0) {
        this.$toast.show({
          message: this.$t('leadList.reacquisition.loading'),
          type: 'info',
        });
        await this.getCallTargetsAndMeta();
        this.$toast.show({
          message: this.$t('leadList.reacquisition.done'),
          type: 'success',
        });
      } else {
        await this.getCallTargetsAndMeta();
      }
    },
    hiddenMailForm() {
      this.mailFormVisible = false;
    },
    async handleResetColumns() {
      await this.setDefaultDisplayColumns();
    },
    async handleUpdateDisplayColumns(columns: string[]) {
      await this.doActionWithWait('saveAsDefaultWait', async () => {
        await this.updateDisplayColumns(columns);
        this.$toast.show({ message: this.$t('leadList.saveAsDefault.success') });
      }).catch(() => {
        this.$toast.show({ message: this.$t('leadList.saveAsDefault.error') });
      });
    },
    setLeadViewSettings(leadView) {
      if (typeof leadView !== 'object' || Object.keys(this.leadView).length === 0) {
        return false;
      }
      const columns = this.$JSON.parse(leadView.displayColumns);
      this.setLeadListSelectedColumnsAction(columns);
      this.setLeadListSortKeyAction(leadView.sortKey);
      this.setLeadListSortOrderAction(leadView.sortOrder);
      // NOTE: リードビューとローカルフィルターパラメータ値で同じキーがある場合は、リードビューの値をカスタムしているケースと考えられるため、ローカルフィルターパラメータ値を優先して設定する。
      this.setFilterParams(lodash.merge(this.$JSON.parse(leadView.filterParams), this.leadListFilterParams));
    },
    setupTaskTimer() {
      // ストアの各Tasksを見て、対応するpendingTaskIds配列に存在しながらも、すでに isSuccess 状態になってしまった task が存在する場合、
      // 対応するpendingTaskIds配列から 該当の taskId を削除したうえで、リードの再取得処理を行う
      this.taskTimerObj = this.$setInterval(() => {
        if (!this.existSomePendingTasks) return false;

        const taskTypes = this.makeTaskTypes();
        for (const taskType of taskTypes) {
          const isExistSuccessfulPendingTask = this.tasksProcedure(taskType);
          if (isExistSuccessfulPendingTask) {
            this.getCallTargets();
            break;
          }
        }
      }, 2000);
    },
    existSomePendingTasks() {
      return (
        this.pendingCsvImportTaskIds.length > 0
        || this.pendingCsvOverwriteTaskIds.length > 0
        || this.pendingExcelImportTaskIds.length > 0
        || this.pendingSfImportTaskIds.length > 0
      );
    },
    makeTaskTypes() {
      return [
        {
          targetTasks: this.leadCsvImportTasks,
          pendingTaskIds: this.pendingCsvImportTaskIds,
          setAction: this.setPendingCsvImportTaskIdsAction,
        },
        {
          targetTasks: this.leadCsvOverwriteTasks,
          pendingTaskIds: this.pendingCsvOverwriteTaskIds,
          setAction: this.setPendingCsvOverwriteTaskIdsAction,
        },
        {
          targetTasks: this.leadExcelImportTasks,
          pendingTaskIds: this.pendingExcelImportTaskIds,
          setAction: this.setPendingExcelImportTaskIdsAction,
        },
        {
          targetTasks: this.leadExcelOverwriteTasks,
          pendingTaskIds: this.pendingExcelOverwriteTaskIds,
          setAction: this.setPendingExcelOverwriteTaskIdsAction,
        },
        {
          targetTasks: this.sfReportImportTasks,
          pendingTaskIds: this.pendingSfImportTaskIds,
          setAction: this.setPendingSfImportTaskIdsAction,
        },
      ];
    },
    tasksProcedure({ targetTasks, pendingTaskIds, setAction }) {
      const successfulPendingTaskIds = this.extractSuccessfulTasks(targetTasks, pendingTaskIds);
      if (successfulPendingTaskIds.length > 0) {
        this.removeSpecificTaskIdsFromPendingTasks(pendingTaskIds, successfulPendingTaskIds, setAction);
        return true;
      } else {
        return false;
      }
    },
    extractSuccessfulTasks(targetTasks, pendingTaskIds) {
      const successfulPendingTaskIds = [];
      if (!Array.isArray(targetTasks)) return successfulPendingTaskIds;
      const pendingTasks = targetTasks.filter((task) => pendingTaskIds.includes(task.id));
      pendingTasks.forEach((task) => {
        if (this.isSuccessTask(task)) successfulPendingTaskIds.push(task.id);
      });
      return successfulPendingTaskIds;
    },
    removeSpecificTaskIdsFromPendingTasks(pendingTaskIds, successfulPendingTaskIds, setAction) {
      if (!Array.isArray(pendingTaskIds)) return;
      const newPendingIds = pendingTaskIds.filter((id) => !successfulPendingTaskIds.includes(id));
      setAction(newPendingIds);
    },
    isSuccessTask(task) {
      const existTargetTask = typeof task.id === 'number';
      const existFinishedAt = typeof task.finishedAt === 'string' && task.finishedAt.length > 0;
      const noError = task.errorData == null;
      return existTargetTask && existFinishedAt && noError;
    },
    setFilterParams(filterParams) {
      if (filterParams == null) return false;
      filterParams = camelcaseFilterParamsKeys(filterParams);
      if (Object.keys(filterParams).length > 0) {
        this.setLeadListFilterParamsAction(filterParams);
        this.setFilterControlPanelVisibleAction(true);
      }
    },
    showImportModal(modal: string) {
      this.isImportMenuVisible = false;
      this.$modal.show(modal);
    },
    showOverwriteModal(modal: string) {
      this.isOverwriteMenuVisible = false;
      this.$modal.show(modal);
    },
    showDeleteModal(modal: string) {
      this.isDeleteMenuVisible = false;
      this.$modal.show(modal);
    },
    async showBulkMailForm() {
      this.mailFormVisible = true;
      this.$nextTick(() => {
        this.bulkMail.leadViewId = this.leadView.id;
      });
    },
    handleSelectedCallTargetIdsUpdate(selectedCallTargetIds: number[]) {
      this.selectedCallTargetIds = selectedCallTargetIds;
    },
    handleClickBulkUpdate(itemId: string) {
      if (itemId === 'operator') {
        this.$modal.show('bulkAssignOperator');
      } else if (itemId === 'freeTag') {
        this.$modal.show('bulkUpdateFreeTags');
      } else if (itemId === 'leadSource') {
        this.$modal.show('bulkUpdateLeadSources');
      }
    },
    async handleSelectLeadView(leadView: LeadView | null) {
      this.setLeadListFilterParamsAction({});
      if (leadView === null) {
        await this.linkToOrReload({ name: 'LeadList' });
        return;
      }
      await this.linkToOrReload({ name: 'LeadList', query: { leadViewId: leadView.id } });
      await this.logApi.postLeadViewLog({
        request: {
          leadViewLogBody: {
            leadViewId: leadView.id,
            event: 'click',
          },
        },
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.footer-content {
  position: fixed;
  bottom: 0;
  width: 100%;
}

.save-title {
  color: $basecolor-primary;
}

.save-text {
  margin: 0;
  text-align: center;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.lead-view-area {
  width: 100%;
  height: 34px;

  &.loaded {
    display: flex;
  }
}

.list-item {
  display: block;
  color: $textcolor-base;
  line-height: 2;
  text-decoration: none;
  transition: $transition-base;
  &:hover {
    opacity: 0.4;
  }
  &.router-link-active {
    font-weight: 600;
  }
}

.call-list-template {
  @include m-fixed-height(100%);
  overflow-y: hidden;
}


.lead-list-area {
  @include m-fixed-height(calc(100% - 60px));
  overflow-y: auto;
  &.with-footer {
    // NOTE: footerぶん引く
    @include m-fixed-height(calc(100% - 60px - 72px));
  }
}

:deep(.list-name-width > i.b-icon) {
  margin-left: 8px;
  margin-bottom: -3px;
}

.lead-delete {
  color: $basecolor-secondary;
}

.update-icon {
  margin-left: 8px;
  margin-right: 8px;
}
.icon-text {
  font-size: $fontsize-100;
}
.content {
  position: relative;

  & > :deep(.el-loading-mask) {
    z-index: 3; // NOTE: リードリスト < リードリスト上のローディング < これ
  }
}
.toggle-btn {
  position: relative;
}
.toggle-icon {
  position: absolute;
  left: 6px;
  transform: rotate(90deg);
  &.is-open {
    transform: rotate(180deg);
  }
}
</style>
